import React, { useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { Tooltip, Input } from "antd";

import {
  useAllOrders,
  useDownloadManifest,
  useSearchOrders,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Manifested = () => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { setLoading } = useLoader();

  // Fetch all orders (paginated)
  const {
    data,
    isLoading,
    refetch: refetchAll,
  } = useAllOrders(
    { currentPage, ORDERS_PER_PAGE },
    {
      enabled: !searchQuery,
    }
  );

  // Format createdAt properly
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Fetch searched orders
  const { data: searchData, isLoading: isSearchLoading } = useSearchOrders(
    searchQuery,
    { enabled: !!searchQuery }
  );

  const { mutate: downloadManifest, isLoading: downloadingManifest } =
    useDownloadManifest();

  // Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Refetch all orders when search is cleared
  useEffect(() => {
    if (!searchQuery) {
      setLoading(true);
      refetchAll().finally(() => setLoading(false));
    }
  }, [searchQuery, refetchAll, setLoading]);

  // Pick correct dataset
  const orders = searchQuery ? searchData || [] : data?.orders || [];

  // Only keep manifested + shipped orders
  const filteredOrders = Array.isArray(orders)
    ? orders.filter((order) => order?.manifestId && order?.shippingStatus)
    : [];
  const sortedOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const totalPages = searchQuery
    ? 1
    : Math.ceil(sortedOrders.length / ORDERS_PER_PAGE);

  const paginatedOrders = searchQuery
    ? sortedOrders
    : sortedOrders.slice(
        (currentPage - 1) * ORDERS_PER_PAGE,
        currentPage * ORDERS_PER_PAGE
      );

  const isLoadingCombined = isLoading || (searchQuery && isSearchLoading);

  // Group orders by manifestId
  // Group orders by manifestId
  const groupedOrders = paginatedOrders.reduce((acc, order) => {
    if (!order.manifestId) return acc;
    if (!acc[order.manifestId]) acc[order.manifestId] = [];
    acc[order.manifestId].push(order);
    return acc;
  }, {});

  // Convert grouped object to array and sort by latest createdAt
  const sortedGroupedOrders = Object.entries(groupedOrders).sort(
    ([_aId, aOrders], [_bId, bOrders]) => {
      const aDate = new Date(
        Math.max(...aOrders.map((o) => new Date(o.createdAt).getTime()))
      );
      const bDate = new Date(
        Math.max(...bOrders.map((o) => new Date(o.createdAt).getTime()))
      );
      return bDate - aDate; // latest first
    }
  );

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[900px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Manifest ID</th>
              <th className="p-3">Orders</th>
              <th className="p-3">Courier Partner</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoadingCombined ? (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : sortedGroupedOrders.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-400">
                  No manifested orders found.
                </td>
              </tr>
            ) : (
              sortedGroupedOrders.map(([manifestId, manifestOrders]) => {
                // get the latest order for display
                const latestOrder = manifestOrders.reduce((latest, order) =>
                  new Date(order.createdAt) > new Date(latest.createdAt)
                    ? order
                    : latest
                );

                return (
                  <tr key={manifestId} className="hover:bg-gray-50">
                    {/* Order Date */}
                    <td className="p-4">
                      {latestOrder?.createdAt
                        ? formatDate(latestOrder.createdAt)
                        : "N/A"}
                    </td>

                    {/* Manifest ID */}
                    <td className="p-4 text-blue-600 font-medium">
                      {manifestId || "N/A"}
                    </td>

                    {/* Orders count */}
                    <td className="p-4 font-semibold text-gray-700">
                      {manifestOrders.length} Orders
                    </td>

                    {/* Courier Partner */}
                    <td className="p-4 text-gray-700">
                      {typeof latestOrder?.courier === "string"
                        ? latestOrder.courier
                        : latestOrder?.courier?.name || "N/A"}
                    </td>

                    {/* Manifest Status */}
                    <td className="p-4">
                      {latestOrder?.manifestStatus === "manifest_downloaded" ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                          Manifest Downloaded
                        </span>
                      ) : latestOrder?.manifestStatus === "manifested" ? (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700">
                          Manifest Generated
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                          {latestOrder?.manifestStatus || "N/A"}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="p-4 space-x-2">
                      <Tooltip title="Download Manifest">
                        <button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                          onClick={() => downloadManifest(manifestId)}
                          disabled={downloadingManifest}
                        >
                          <FileDown size={16} />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!searchQuery && !isLoadingCombined && totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manifested;
